import { Platform, ViewStyle } from 'react-native';
import { isRTL as isRtlLayout } from '../i18n/rtl';
import { useThemeStore } from './store';

const getColors = () => useThemeStore.getState().colors;

/**
 * SPACING & BREAKPOINTS
 */
const spacing = Object.freeze({
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  us: 2,
});

const breakpoints = Object.freeze({
  mobile: 320,
  tablet: 640,
  desktop: 1280,
});

/**
 * BORDERS & RADII
 */
const borderRadius = Object.freeze({
  xs: 4,
  sm: 5,
  md: 10,
  lg: 16,
  xl: 24,
  pill: 20,
  round: 999,
});

const borderWidth = Object.freeze({ default: 1, thick: 2 });

/**
 * TYPOGRAPHY
 */
// const fontFamily = {
//   heading: Platform.select({ ios: 'OpenSans-Bold', android: 'OpenSans-Bold' }),
//   body: Platform.select({ ios: 'Roboto', android: 'Roboto' }),
//   bodyLight: Platform.select({ ios: 'Roboto-Light', android: 'sans-serif-light' }),
// };

const lineHeightMultiple = 1.11;
const calcLineHeight = (size: number) => Math.round(size * lineHeightMultiple);

const typography = Object.freeze({
  display: {
    fontSize: 40,
    lineHeight: 54,
    fontWeight: '700' as const,
  },
  h1: { fontSize: 32, fontWeight: '700' as const, lineHeight: 40 },
  h2: { fontSize: 24, fontWeight: '600' as const, lineHeight: 32 },
  h3: { fontSize: 20, fontWeight: '600' as const, lineHeight: 28 },
  headline: {
    fontSize: 30,
    lineHeight: calcLineHeight(30),
    fontWeight: '600' as const,
  },
  title: {
    fontSize: 24,
    lineHeight: calcLineHeight(24),
    fontWeight: '600' as const,
  },
  subtitle: { fontSize: 18, fontWeight: '600' as const, lineHeight: 24 },
  body: {
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '400' as const,
  },
  bodySmall: { fontSize: 14, fontWeight: '400' as const, lineHeight: 20 },
  bodyMedium: { fontSize: 14, fontWeight: '500' as const, lineHeight: 20 },
  label: {
    fontSize: 20,
    lineHeight: calcLineHeight(20),
    fontWeight: '600' as const,
  },
  caption: {
    fontSize: 14,
    lineHeight: calcLineHeight(14),
    fontWeight: '400' as const,
  },
  captionSmall: { fontSize: 10, fontWeight: '600' as const, lineHeight: 14, letterSpacing: 0.5 },
  captionMini: { fontSize: 12, fontWeight: '500' as const, lineHeight: 14 },
  headerTitle: { fontSize: 18, fontWeight: '600' as const, lineHeight: 18 },
});

/**
 * DIMENSIONS & SIZES
 */
const sizes = Object.freeze({
  iconButton: 40,
  smallIcon: 20,
  avatar: 80,
  barHeight: 40,
  counterBox: 8,
  inputHeight: 48,
  inputHeightLarge: 72,
  breadcrumbHeight: 44,
  iconSizeSm: 16,
  iconSizeMd: 24,
  iconSizeLg: 32,
  buttonHeightSmall: 32,
  buttonHeightMedium: 40,
  buttonHeightLarge: 48,
});

/**
 * SHADOWS
 */
const shadowOpacity = 0.15;

const iosShadow = (radius: number, offsetY: number, opacity: number = shadowOpacity): ViewStyle => ({
  shadowColor: getColors().shadowColor || '#000',
  shadowOffset: { width: 0, height: offsetY },
  shadowOpacity: opacity,
  shadowRadius: radius,
});

const shadows = Object.freeze({
  none: {} as ViewStyle,
  xs: Platform.select<ViewStyle>({
    ios: iosShadow(10, 1, 0.05),
    android: { elevation: 0.5 },
  }) || {},
  sm: Platform.select<ViewStyle>({
    ios: iosShadow(1, 1, 0.05),
    android: { elevation: 1 },
  }) || {},
  md: Platform.select<ViewStyle>({
    ios: iosShadow(1, 0.5, 0.05),
    android: { elevation: 2 },
  }) || {},
  lg: Platform.select<ViewStyle>({
    ios: iosShadow(5, 2, 0.2),
    android: { elevation: 4 },
  }) || {},
  xl: Platform.select<ViewStyle>({
    ios: iosShadow(20, 24, 0.3),
    android: { elevation: 6 },
  }) || {},
  card: Platform.select<ViewStyle>({
    ios: iosShadow(6, 2, 0.15),
    android: { elevation: 3 },
  }) || {},
  modal: Platform.select<ViewStyle>({
    ios: iosShadow(16, -4, 0.15),
    android: { elevation: 6 },
  }) || {},
  button: Platform.select<ViewStyle>({
    ios: iosShadow(5, 2, 0.1),
    android: { elevation: 2 },
  }) || {},
});

const animations = Object.freeze({
  duration: Object.freeze({ fast: 150, normal: 300, slow: 500 }),
  easing: Object.freeze({ easeInOut: 'ease-in-out', easeIn: 'ease-in', easeOut: 'ease-out' }),
  staggerDelay: 50,
});

/**
 * Touch target expansion (`hitSlop` on `Pressable` / `Clickable`). Expand compact visuals so
 * effective bounds approach ~44×44 pt (Apple HIG / Material accessibility targets).
 * - **small**: dense lists, horizontal rails where wide slop would steal neighbor taps
 * - **medium**: row controls, icon buttons, primary actions
 * - **large**: toolbar / modal chrome when layout allows
 */
const hitSlop = Object.freeze({
  small: Object.freeze({ top: 12, bottom: 12, left: 12, right: 12 }),
  medium: Object.freeze({ top: 16, bottom: 16, left: 16, right: 16 }),
  large: Object.freeze({ top: 20, bottom: 20, left: 20, right: 20 }),
});

/**
 * CARD DESIGN TOKENS (unified card styling: dashboard, address, community, etc.)
 */
const cardPadding = Object.freeze({ sm: 8, lg: 16, xl: 24 });
const cardRadius = 16;
const contentCardShadow: ViewStyle = {
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.06,
  shadowRadius: 6,
  elevation: 2,
};
const cardTokens = Object.freeze({
  radius: cardRadius,
  padding: cardPadding,
  shadow: contentCardShadow,
});

/**
 * INPUT DESIGN TOKENS (unified text input styling app-wide)
 */
const inputTokens = Object.freeze({
  height: 48,
  minHeight: 48,
  paddingHorizontal: 12,
  paddingVertical: 12,
  borderRadius: 10,
  borderWidth: 1,
  fontSize: 16,
});

/**
 * DROPDOWN / PICKER DESIGN TOKENS (select fields, area picker, slot picker)
 */
const pickerTokens = Object.freeze({
  borderRadius: 10,
  borderWidth: 1,
  paddingHorizontal: 12,
  paddingVertical: 12,
  optionPaddingVertical: 14,
  optionPaddingHorizontal: 16,
  dropdownMaxHeight: 280,
});

/**
 * MODAL / BOTTOM SHEET TOKENS (area picker popup, list pickers)
 */
const modalTokens = Object.freeze({
  sheetBorderRadius: 20,
  /** Max sheet height; content scrolls inside when taller (see `BottomSheetPopup`). */
  sheetMaxHeight: '88%',
  headerPadding: 12,
  /** Space between sheet header hairline and body content. */
  bodyPaddingTop: 12,
  /** Space above footer hairline (below scroll/grid content). */
  footerMarginTop: 12,
  /** Space between footer hairline and action buttons. */
  footerPaddingTop: 12,
  overlayOpacity: 0.5,
});

export const appTheme = {
  get colors() {
    return getColors();
  },
  get isRTL() {
    return isRtlLayout();
  },
  spacing,
  breakpoints,
  borderRadius,
  borderWidth,
  typography,
  sizes,
  shadows,
  animations,
  hitSlop,
  card: cardTokens,
  input: inputTokens,
  picker: pickerTokens,
  modal: modalTokens,
  overlay: 'rgba(0,0,0,0.5)' as const,
};

export default appTheme;
